import React, { useCallback, useMemo, useState } from 'react';

import { Separator, Text } from '@change/ds-core-ui';
import { AiGeneratedTextIcon, CheckCircleOutlineIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { parseHtml } from '@change-corgi/core/react/html';
import { Translate, useI18n, withI18nPlaceholders } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import type { EmptyProps } from '@change-corgi/core/types';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/petitionDetails/petition-details.yml';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from 'src/app/pages/petitionDetails/pageContext';

import { AiSummaryFeedbackLinksStaggered } from './components/AiSummaryFeedbackLinksStaggered';
import { useAiSummaryWordAnimation } from './hooks/useAiSummaryWordAnimation';
import { storeFeedback, useHasStoredFeedback } from './utils/feedbackStorageUtils';
import { parseAiSummaryBullets } from './utils/parseAiSummaryBullets';

// oxlint-disable-next-line max-lines-per-function
const AiSummaryComponent: React.FC<Props> = () => {
	const track = useTracking();
	const { translate } = useI18n();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { aiSummaries },
	} = usePetitionDetailsPageUserData();
	const storedFeedback = useHasStoredFeedback(petitionId);
	const [feedbackSubmittedForPetitionId, setFeedbackSubmittedForPetitionId] = useState<string | null>(null);
	const justSubmitted = feedbackSubmittedForPetitionId === petitionId;

	const shouldAnimatePrompt = storedFeedback === false && !justSubmitted;
	const feedbackPromptWords = useMemo(
		() => (shouldAnimatePrompt ? translate('corgi.petition_details.ai_summary.feedback_prompt').split(/\s+/) : []),
		[shouldAnimatePrompt, translate],
	);
	const { animationComplete, visibleWords } = useAiSummaryWordAnimation(
		shouldAnimatePrompt,
		feedbackPromptWords.length,
	);

	const handleFeedback = useCallback(
		(helpful: boolean) => {
			setFeedbackSubmittedForPetitionId(petitionId);
			storeFeedback(petitionId);
			void track('petition_page_ai_summary_feedback', {
				ai_summary_marked_helpful: helpful,
				petition_id: petitionId,
			});
		},
		[petitionId, track],
	);

	const summary = aiSummaries?.summary;
	const bullets = useMemo(() => parseAiSummaryBullets(summary), [summary]);

	if (!bullets.length) {
		return null;
	}

	return (
		<section aria-labelledby="ai-summary-heading" data-qa="ai-summary-section" data-testid="ai-summary-section">
			<div className="-mt-4.5 mb-6 rounded-lg border border-[#DDE0E9] bg-[linear-gradient(180deg,#FFF_0%,#F0F7FF_100%)] p-6 md:mt-0 md:mb-[40px]">
				<div className="flex flex-col gap-4">
					<div className="flex items-center gap-2">
						<AiGeneratedTextIcon aria-hidden className="text-[#0D4DFB]" size="small" />
						<Text
							className="text-typography-strong"
							fontWeight="bold"
							id="ai-summary-heading"
							size="x-large"
							variant="body"
						>
							<Translate value="corgi.petition_details.ai_summary.section_description_label" />
						</Text>
					</div>
					<Text as="div" className="text-typography-strong" size="medium" variant="body">
						<ul className="m-0 flex list-disc flex-col gap-1 pl-6">
							{bullets.map((bullet) => (
								<li key={bullet}>{parseHtml(bullet)}</li>
							))}
						</ul>
					</Text>
					<Separator role="presentation" />
					<div className="flex min-h-[24px] flex-wrap items-center justify-between gap-y-2">
						<div className="flex flex-wrap items-center gap-x-3 gap-y-2">
							<Text as="p" className="text-typography-weak" size="small" variant="body">
								<Translate value="corgi.petition_details.ai_summary.feedback_source" />
								{shouldAnimatePrompt && (
									<>
										{' '}
										{feedbackPromptWords.map((word, index) => (
											<span
												key={index}
												className={cn(
													'inline-block transition-all duration-500 ease-out',
													index < visibleWords ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0',
												)}
											>
												{word}
												{index < feedbackPromptWords.length - 1 && '\u00A0'}
											</span>
										))}
									</>
								)}
							</Text>
							{animationComplete && <AiSummaryFeedbackLinksStaggered onFeedback={handleFeedback} />}
						</div>
						{justSubmitted && (
							<div className="flex items-center gap-1">
								<CheckCircleOutlineIcon aria-hidden className="text-(--icon-success,#0F8110)" size="small" />
								<Text as="p" className="text-typography-weak" size="small" variant="body">
									<Translate value="corgi.petition_details.ai_summary.feedback_received" />
								</Text>
							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	);
};

export const AiSummary = withI18nPlaceholders(I18N_PLACEHOLDERS, AiSummaryComponent);

type Props = EmptyProps;
